import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Download,
  CheckCircle,
  Error as ErrorIcon,
  Edit,
} from '@mui/icons-material';
import { Transaction, LoyaltyProgram } from '../../types';
import Papa from 'papaparse';

interface BulkTransactionUploadProps {
  open: boolean;
  onClose: () => void;
  onUpload: (transactions: Omit<Transaction, 'id'>[]) => Promise<void>;
  programs: LoyaltyProgram[];
}

interface ParsedTransaction {
  date: string;
  description: string;
  amount: number;
  type: 'earned' | 'redeemed';
  category?: string;
  programId?: string;
  programName?: string;
  error?: string;
}

const BulkTransactionUpload: React.FC<BulkTransactionUploadProps> = ({
  open,
  onClose,
  onUpload,
  programs,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [parsedTransactions, setParsedTransactions] = useState<ParsedTransaction[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const steps = ['Upload CSV', 'Review & Map', 'Confirm'];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setFile(selectedFile);
    setError(null);
    parseCSVFile(selectedFile);
  };

  const parseCSVFile = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: Papa.ParseResult<any>) => {
        try {
          const transactions = results.data.map((row: any, index: number) => {
            const transaction: ParsedTransaction = {
              date: row.date || row.Date || row.DATE || '',
              description: row.description || row.Description || row.DESCRIPTION || row.merchant || row.Merchant || '',
              amount: parseFloat(row.amount || row.Amount || row.AMOUNT || row.points || row.Points || '0'),
              type: (row.type || row.Type || row.TYPE || 'earned').toLowerCase() === 'redeemed' ? 'redeemed' : 'earned',
              category: row.category || row.Category || row.CATEGORY || 'general',
              programName: row.program || row.Program || row.PROGRAM || row.card || row.Card || '',
            };

            // Validate
            if (!transaction.date) {
              transaction.error = 'Missing date';
            } else if (isNaN(transaction.amount) || transaction.amount === 0) {
              transaction.error = 'Invalid amount';
            }

            // Try to match program
            if (transaction.programName) {
              const matchedProgram = programs.find(p => 
                p.name.toLowerCase().includes(transaction.programName!.toLowerCase()) ||
                transaction.programName!.toLowerCase().includes(p.name.toLowerCase())
              );
              if (matchedProgram) {
                transaction.programId = matchedProgram.id;
              } else {
                transaction.error = transaction.error 
                  ? `${transaction.error}; Program not found` 
                  : 'Program not found';
              }
            } else {
              transaction.error = transaction.error 
                ? `${transaction.error}; Missing program` 
                : 'Missing program';
            }

            return transaction;
          });

          setParsedTransactions(transactions);
          setActiveStep(1);
        } catch (err) {
          setError('Failed to parse CSV file. Please check the format.');
          console.error('CSV parse error:', err);
        }
      },
      error: (err: Error) => {
        setError(`Failed to read CSV: ${err.message}`);
      },
    });
  };

  const handleProgramChange = (index: number, programId: string) => {
    const updated = [...parsedTransactions];
    updated[index].programId = programId;
    updated[index].error = updated[index].error?.replace('Program not found', '').replace('; ', '').trim() || undefined;
    setParsedTransactions(updated);
  };

  const handleEditTransaction = (index: number, field: keyof ParsedTransaction, value: any) => {
    const updated = [...parsedTransactions];
    (updated[index] as any)[field] = value;
    setParsedTransactions(updated);
  };

  const handleDeleteTransaction = (index: number) => {
    setParsedTransactions(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    const validTransactions = parsedTransactions.filter(t => !t.error && t.programId);
    
    if (validTransactions.length === 0) {
      setError('No valid transactions to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const transactions: Omit<Transaction, 'id'>[] = validTransactions.map(t => ({
        programId: t.programId!,
        date: new Date(t.date),
        description: t.description,
        type: t.type,
        points: t.amount,
        balance: 0, // Will be calculated by the service
        category: t.category,
      }));

      // Simulate progress
      for (let i = 0; i <= transactions.length; i++) {
        setUploadProgress((i / transactions.length) * 100);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      await onUpload(transactions);
      
      handleClose();
    } catch (err) {
      setError('Failed to upload transactions. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFile(null);
    setParsedTransactions([]);
    setError(null);
    setUploading(false);
    setUploadProgress(0);
    onClose();
  };

  const downloadTemplate = () => {
    const template = `date,description,amount,type,program,category
2024-01-15,Restaurant Purchase,500,earned,Chase Sapphire,dining
2024-01-20,Flight Redemption,25000,redeemed,Delta SkyMiles,travel
2024-01-25,Online Shopping,1200,earned,American Express,shopping`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transaction_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const validCount = parsedTransactions.filter(t => !t.error).length;
  const errorCount = parsedTransactions.filter(t => t.error).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Bulk Transaction Upload</Typography>
          <Button
            startIcon={<Download />}
            onClick={downloadTemplate}
            size="small"
            variant="outlined"
          >
            Download Template
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Upload CSV */}
        {activeStep === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a CSV file with columns: <strong>date, description, amount, type, program, category</strong>
              <br />
              <Typography variant="caption">
                • date: YYYY-MM-DD format
                <br />
                • type: "earned" or "redeemed"
                <br />
                • program: Must match one of your programs
              </Typography>
            </Alert>

            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
              onClick={() => document.getElementById('csv-upload')?.click()}
            >
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {file ? file.name : 'Click to upload CSV file'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or drag and drop your CSV file here
              </Typography>
            </Box>
          </Box>
        )}

        {/* Step 2: Review & Map */}
        {activeStep === 1 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip
                icon={<CheckCircle />}
                label={`${validCount} Valid`}
                color="success"
                variant="outlined"
              />
              <Chip
                icon={<ErrorIcon />}
                label={`${errorCount} Errors`}
                color="error"
                variant="outlined"
              />
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Program</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedTransactions.map((txn, index) => (
                    <TableRow key={index} sx={{ bgcolor: txn.error ? 'error.light' : 'inherit' }}>
                      <TableCell>
                        <TextField
                          type="date"
                          value={txn.date}
                          onChange={(e) => handleEditTransaction(index, 'date', e.target.value)}
                          size="small"
                          sx={{ width: 130 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={txn.description}
                          onChange={(e) => handleEditTransaction(index, 'description', e.target.value)}
                          size="small"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={txn.amount}
                          onChange={(e) => handleEditTransaction(index, 'amount', parseFloat(e.target.value))}
                          size="small"
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          value={txn.type}
                          onChange={(e) => handleEditTransaction(index, 'type', e.target.value)}
                          size="small"
                          SelectProps={{ native: true }}
                          sx={{ width: 100 }}
                        >
                          <option value="earned">Earned</option>
                          <option value="redeemed">Redeemed</option>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          value={txn.programId || ''}
                          onChange={(e) => handleProgramChange(index, e.target.value)}
                          size="small"
                          SelectProps={{ native: true }}
                          sx={{ width: 150 }}
                        >
                          <option value="">Select Program</option>
                          {programs.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {txn.error ? (
                          <Chip label="Error" color="error" size="small" />
                        ) : (
                          <Chip label="Valid" color="success" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleDeleteTransaction(index)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Step 3: Uploading Progress */}
        {uploading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Uploading Transactions...
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {Math.round(uploadProgress)}% Complete
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        {activeStep === 0 && (
          <Button onClick={() => document.getElementById('csv-upload')?.click()} variant="contained">
            Select File
          </Button>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)}>
              Back
            </Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={validCount === 0 || uploading}
            >
              Upload {validCount} Transaction{validCount !== 1 ? 's' : ''}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkTransactionUpload;
